import { useCallback, useEffect, useRef } from 'react'

// Debounced-callback hook used by the contenteditable editors. Each editor
// previously hand-rolled the same `setTimeout` + ref + cleanup dance, with
// imperative `flush()` on blur and `cancel()` on undo / clear. They now share
// this hook so the timing is consistent and timer leaks are impossible.
//
// Usage:
//   const debounced = useDebouncedCallback(() => onChange(buildNext()), 250)
//   onInput  ⇒ debounced.schedule()
//   onBlur   ⇒ debounced.flush()      // fires immediately, drops the timer
//   onUndo   ⇒ debounced.cancel()     // just drops the timer
//
// The latest `cb` is held in a ref so callers don't need to memoise it; each
// fire reads the most recent closure (capturing the latest props / state).
export interface DebouncedCallback {
  schedule: () => void
  flush: () => void
  cancel: () => void
}

export function useDebouncedCallback(cb: () => void, delay: number): DebouncedCallback {
  const cbRef = useRef(cb)
  cbRef.current = cb
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const schedule = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      cbRef.current()
    }, delay)
  }, [delay])

  const flush = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    cbRef.current()
  }, [])

  const cancel = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
  }, [])

  // Drop any pending timer on unmount so it doesn't fire against a stale
  // closure / unmounted component.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  return { schedule, flush, cancel }
}
